import { useEffect, useRef, useState } from "react";
import type {
  GeneratedReleaseNotes,
  GenerateReleaseNotesRequest,
} from "@insights/contracts";
import { generateReleaseNotes } from "../../../api/services/generated-release-notes";
import { copyGeneratedReleaseNotes } from "../helpers/copy-release-notes";

export type ReleaseNotesCopyStatus =
  | "generating"
  | "copied"
  | "ready"
  | "failed";

export function useCopyReleaseNotes(
  workspaceId: string | undefined,
  configurationKey: string,
) {
  const [state, setState] = useState<{
    key: string;
    status: ReleaseNotesCopyStatus;
  }>();
  const [message, setMessage] = useState<string>();
  const busy = useRef(false);
  const ready = useRef<
    { key: string; notes: GeneratedReleaseNotes } | undefined
  >(undefined);

  function selectionKey(selection: GenerateReleaseNotesRequest) {
    return JSON.stringify([workspaceId, configurationKey, selection]);
  }

  useEffect(() => {
    if (state?.status !== "copied") {
      return;
    }
    const timeout = window.setTimeout(() => setState(undefined), 2000);
    return () => window.clearTimeout(timeout);
  }, [state]);

  async function copy(selection: GenerateReleaseNotesRequest) {
    if (!workspaceId || busy.current) {
      return;
    }
    const key = selectionKey(selection);
    busy.current = true;
    setMessage(undefined);
    let notes = ready.current?.key === key ? ready.current.notes : undefined;
    if (!notes) {
      setState({ key, status: "generating" });
      try {
        notes = await generateReleaseNotes(workspaceId, selection);
      } catch (error) {
        setState({ key, status: "failed" });
        setMessage(
          error instanceof Error
            ? error.message
            : "Release notes generation failed. Try again.",
        );
        busy.current = false;
        return;
      }
    }
    try {
      await copyGeneratedReleaseNotes(notes);
      ready.current = undefined;
      setState({ key, status: "copied" });
    } catch {
      ready.current = { key, notes };
      setState({ key, status: "ready" });
      setMessage(
        "Release notes are ready. Click Copy again to allow clipboard access. If copying remains blocked, check your browser permissions.",
      );
    } finally {
      busy.current = false;
    }
  }

  function status(selection: GenerateReleaseNotesRequest | undefined) {
    return selection && state?.key === selectionKey(selection)
      ? state.status
      : undefined;
  }

  return {
    copy,
    status,
    message,
    dismiss: () => setMessage(undefined),
    isGenerating: state?.status === "generating",
  };
}
